/* The Slab Bro – interactive 3D viewer for Shopify sections.
   Markup: any element with [data-slab-viewer] containing a <canvas>, a status element
   [data-status], buttons [data-action="toggle"], [data-color="black|white"], [data-action="reset"]
   and an optional range input [data-angle]. The GLB link comes from data-model. */
(function () {
  'use strict';
  var CDN = 'https://cdn.jsdelivr.net/npm/three@0.147.0/';
  var LIBS = [
    'build/three.min.js',
    'examples/js/loaders/GLTFLoader.js',
    'examples/js/controls/OrbitControls.js',
    'examples/js/environments/RoomEnvironment.js',
    'examples/js/libs/meshopt_decoder.js'
  ];
  var OPEN_DEG = 90; // fully open, from the prototype video
  var COLORS = {
    white: { tex: 0xbfc3cb, smooth: 0xc9ccd3, rTex: 0.72, rSmooth: 0.32, normal: 0.45 },
    black: { tex: 0x101112, smooth: 0x0c0d0e, rTex: 0.62, rSmooth: 0.26, normal: 0.45 }
  };

  var libsPromise = null;
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = resolve; s.onerror = function () { reject(new Error('Could not load ' + src)); };
      document.head.appendChild(s);
    });
  }
  function loadLibs() {
    if (!libsPromise) {
      libsPromise = LIBS.reduce(function (p, file) {
        return p.then(function () {
          if (file.indexOf('three.min.js') > -1 && window.THREE && window.THREE.WebGLRenderer) return;
          return loadScript(CDN + file);
        });
      }, Promise.resolve());
    }
    return libsPromise;
  }

  function Viewer(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');
    this.status = root.querySelector('[data-status]');
    this.url = root.getAttribute('data-model');
    this.color = root.getAttribute('data-default-color') || 'black';
    this.angle = 0; this.target = 0;
    this.texMats = []; this.smoothMats = [];
    this.visible = true;
    if (!this.url) { this.setStatus(root.getAttribute('data-missing-text') || 'Add the 3D model link in the theme editor.'); return; }
    var self = this;
    loadLibs().then(function () { self.init(); }).catch(function () {
      self.setStatus('The 3D viewer could not load. Refresh the page to try again.');
    });
  }

  Viewer.prototype.setStatus = function (t) {
    if (!this.status) return;
    this.status.textContent = t || '';
    this.status.hidden = !t;
  };

  Viewer.prototype.init = function () {
    var THREE = window.THREE, self = this, root = this.root, canvas = this.canvas;
    var renderer = this.renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 0.9;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    var scene = this.scene = new THREE.Scene();
    scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new THREE.RoomEnvironment(), 0.04).texture;
    var camera = this.camera = new THREE.PerspectiveCamera(32, 1, 0.002, 5);
    var controls = this.controls = new THREE.OrbitControls(camera, canvas);
    controls.enableDamping = true; controls.dampingFactor = 0.08; controls.enablePan = false;
    controls.minDistance = 0.1; controls.maxDistance = 0.8; controls.rotateSpeed = 0.9;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };

    var key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(0.18, 0.55, 0.22); key.castShadow = true; key.shadow.mapSize.set(2048, 2048);
    var sc = key.shadow.camera; sc.left = -0.12; sc.right = 0.12; sc.top = 0.12; sc.bottom = -0.12; sc.near = 0.05; sc.far = 1.2;
    key.shadow.bias = -0.0004; key.shadow.radius = 6;
    scene.add(key);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x8a8f96, 0.35));
    var rim = new THREE.DirectionalLight(0xffffff, 0.6); rim.position.set(-0.3, 0.2, -0.4); scene.add(rim);
    var shadow = this.shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShadowMaterial({ opacity: 0.18 }));
    shadow.receiveShadow = true; scene.add(shadow);

    if ('ResizeObserver' in window) new ResizeObserver(function () { self.resize(); }).observe(root);
    this.resize();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { self.visible = e[0].isIntersecting; }).observe(root);
    }

    this.bindUI();
    var loader = new THREE.GLTFLoader();
    if (window.MeshoptDecoder) loader.setMeshoptDecoder(window.MeshoptDecoder);
    loader.load(this.url, function (gltf) {
      var model = gltf.scene;
      model.traverse(function (o) {
        if (o.isMesh) {
          o.castShadow = true; o.receiveShadow = true;
          if (!o.geometry.attributes.normal) o.geometry.computeVertexNormals();
          var m = o.material; m.envMapIntensity = 0.75;
          if (m.normalMap) {
            m.normalMap.wrapS = m.normalMap.wrapT = THREE.RepeatWrapping; m.normalMap.anisotropy = 8;
            if (self.texMats.indexOf(m) < 0) self.texMats.push(m);
          } else if (self.smoothMats.indexOf(m) < 0) self.smoothMats.push(m);
        }
        if (o.name === 'Top_Hinge') self.hinge = o;
      });
      scene.add(model);
      var box = new THREE.Box3().setFromObject(model);
      shadow.position.set(0, box.min.y - 0.0002, 0); shadow.rotation.set(-Math.PI / 2, 0, 0);
      self.applyColor(self.color); self.frame(); self.setStatus('');
      root.classList.add('is-loaded');
    }, undefined, function () {
      self.setStatus('The 3D model could not load. Check the model link in the theme editor.');
    });

    var last = performance.now();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.reduce = reduce;
    renderer.setAnimationLoop(function (t) {
      var dt = Math.min(0.05, (t - last) / 1000); last = t;
      if (!self.visible) return;
      if (Math.abs(self.target - self.angle) > 0.05) { self.angle += (self.target - self.angle) * (1 - Math.exp(-dt * 7)); self.syncUI(); }
      else if (self.angle !== self.target) { self.angle = self.target; self.syncUI(); }
      if (self.hinge) self.hinge.rotation.z = THREE.MathUtils.degToRad(self.angle);
      controls.update(); renderer.render(scene, camera);
    });
  };

  Viewer.prototype.resize = function () {
    if (!this.renderer) return;
    var w = this.root.clientWidth, h = this.root.clientHeight;
    this.renderer.setSize(w, h, false); this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
  };

  Viewer.prototype.frame = function () {
    var THREE = window.THREE;
    var narrow = this.root.clientWidth < 560 || this.root.clientHeight / this.root.clientWidth > 1.1;
    var d = narrow ? 0.46 : 0.38;
    var dir = new THREE.Vector3(0.42, 0.18, 1).normalize().multiplyScalar(d);
    this.camera.position.copy(dir); this.controls.target.set(0, -0.006, 0); this.controls.update();
  };

  Viewer.prototype.applyColor = function (name) {
    var c = COLORS[name] || COLORS.black; this.color = name;
    this.texMats.forEach(function (m) { m.color.setHex(c.tex).convertSRGBToLinear(); m.roughness = c.rTex; m.normalScale.set(c.normal, c.normal); });
    this.smoothMats.forEach(function (m) { m.color.setHex(c.smooth).convertSRGBToLinear(); m.roughness = c.rSmooth; });
    this.root.querySelectorAll('[data-color]').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-color') === name ? 'true' : 'false'); });
  };

  Viewer.prototype.syncUI = function () {
    var a = Math.round(this.angle);
    if (this.range) this.range.value = a;
    if (this.toggle) this.toggle.textContent = a > OPEN_DEG / 2 ? this.toggle.getAttribute('data-close') : this.toggle.getAttribute('data-open');
  };

  Viewer.prototype.bindUI = function () {
    var self = this, root = this.root;
    this.toggle = root.querySelector('[data-action="toggle"]');
    this.range = root.querySelector('[data-angle]');
    if (this.toggle) this.toggle.addEventListener('click', function () {
      self.target = self.angle > OPEN_DEG / 2 ? 0 : OPEN_DEG; if (self.reduce) self.angle = self.target;
    });
    if (this.range) this.range.addEventListener('input', function () { self.target = self.angle = +self.range.value; });
    root.querySelectorAll('[data-color]').forEach(function (b) {
      b.addEventListener('click', function () { self.applyColor(b.getAttribute('data-color')); });
    });
    var reset = root.querySelector('[data-action="reset"]');
    if (reset) reset.addEventListener('click', function () { self.frame(); self.target = 0; });
  };

  function boot() {
    document.querySelectorAll('[data-slab-viewer]').forEach(function (el) {
      if (!el.__slab) el.__slab = new Viewer(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  // Theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', boot);
})();